<script setup>
import { ref,  onMounted, onBeforeUnmount } from 'vue'
import { db } from '@/firebase'
import {
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy
} from 'firebase/firestore'

// Form state
const nombre = ref('')
const porcentaje = ref(10) // humano: 10, 1.5, etc.
const loading = ref(false)
const errorMsg = ref('')

// List state
const impuestos = ref([])
let unsub = null

const colRef = collection(db, 'impuestos')

function resetForm() {
  nombre.value = ''
  porcentaje.value = 10
}

function validatePct(p) {
  if (p === '' || p === null || isNaN(Number(p))) return 'Ingresa un número'
  if (Number(p) < 0) return 'No puede ser negativo'
  if (Number(p) > 100) return 'No puede superar 100'
  return ''
}

async function crearTax() {
  errorMsg.value = ''
  const err = validatePct(porcentaje.value)
  if (!nombre.value.trim()) return (errorMsg.value = 'Nombre requerido')
  if (err) return (errorMsg.value = err)

  try {
    loading.value = true
    await addDoc(colRef, {
      nombre: nombre.value.trim(),
      porcentaje: Number(porcentaje.value), // guardamos humano (10 / 1.5)
      activo: true,
      creado: new Date()
    })
    resetForm()
  } catch (e) {
    console.error(e)
    errorMsg.value = 'No se pudo crear el impuesto'
  } finally {
    loading.value = false
  }
}

function fmtPctHumano(p) {
  const n = Number(p)
  if (isNaN(n)) return '—'
  return n.toString().replace(/\.0+$/, '')
}

// Edit inline handlers
async function toggleActivo(id, curr) {
  try { await updateDoc(doc(db, 'impuestos', id), { activo: !curr }) } catch(e){ console.error(e) }
}

async function actualizarNombre(id, val) {
  const v = String(val || '').trim()
  if (!v) return
  try { await updateDoc(doc(db, 'impuestos', id), { nombre: v }) } catch(e){ console.error(e) }
}

async function actualizarPorcentaje(id, val) {
  const err = validatePct(val)
  if (err) { alert(err); return }
  try { await updateDoc(doc(db, 'impuestos', id), { porcentaje: Number(val) }) } catch(e){ console.error(e) }
}

async function eliminar(id) {
  if (!confirm('¿Eliminar impuesto?')) return
  try { await deleteDoc(doc(db, 'impuestos', id)) } catch(e){ console.error(e) }
}

onMounted(() => {
  const q = query(colRef, orderBy('nombre'))
  unsub = onSnapshot(q, (snap) => {
    impuestos.value = snap.docs.map(d => ({ id: d.id, ...d.data() }))
  })
})

onBeforeUnmount(() => { if (unsub) unsub() })
</script>

<template>
  <div class="max-w-3xl mx-auto p-4 space-y-6">
    <h2 class="text-2xl font-bold">Impuestos (CRUD en vivo)</h2>

    <!-- Crear -->
    <div class="rounded-2xl shadow p-4 grid gap-3 bg-white">
      <h3 class="font-semibold">Crear Impuesto</h3>
      <input v-model="nombre" placeholder="Nombre del impuesto" class="border rounded p-2" />
      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="text-sm text-gray-600">Porcentaje (%)</label>
          <input v-model.number="porcentaje" type="number" step="0.1" min="0" max="100" class="border rounded p-2 w-full" />
        </div>
        <div class="flex items-end">
          <button :disabled="loading" @click="crearTax" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded w-full">
            {{ loading ? 'Guardando…' : 'Guardar' }}
          </button>
        </div>
      </div>
      <p v-if="errorMsg" class="text-red-600 text-sm">{{ errorMsg }}</p>
      <p class="text-xs text-gray-500">Se guarda como humano (ej: 10 → 10%). En cálculos divide entre 100.</p>
    </div>

    <!-- Listado / edición inline -->
    <div class="rounded-2xl shadow overflow-hidden">
      <table class="min-w-full bg-white">
        <thead class="bg-gray-50">
          <tr>
            <th class="text-left p-3">Nombre</th>
            <th class="text-left p-3">Porcentaje (%)</th>
            <th class="text-left p-3">Activo</th>
            <th class="text-right p-3">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="tax in impuestos" :key="tax.id" class="border-t">
            <td class="p-3">
              <input :value="tax.nombre" @change="e=>actualizarNombre(tax.id, e.target.value)" class="border rounded p-2 w-full" />
            </td>
            <td class="p-3">
              <input :value="fmtPctHumano(tax.porcentaje)" type="number" step="0.1" min="0" max="100"
                     @change="e=>actualizarPorcentaje(tax.id, e.target.value)" class="border rounded p-2 w-full" />
            </td>
            <td class="p-3">
              <label class="inline-flex items-center gap-2">
                <input type="checkbox" :checked="tax.activo !== false" @change="()=>toggleActivo(tax.id, tax.activo !== false)" />
                <span class="text-sm">{{ (tax.activo !== false) ? 'Sí' : 'No' }}</span>
              </label>
            </td>
            <td class="p-3 text-right">
              <button @click="eliminar(tax.id)" class="text-red-600 hover:text-red-700">Eliminar</button>
            </td>
          </tr>
          <tr v-if="impuestos.length===0">
            <td colspan="4" class="p-6 text-center text-gray-500">Sin impuestos</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Nota de uso en cálculos -->
    <div class="text-sm text-gray-600">
      <p><strong>Cómo aplicar en cálculos:</strong> <code>montoConImpuesto = base * (1 + (tax.porcentaje/100))</code></p>
    </div>
  </div>
</template>

<style scoped>
/* simple styling */
</style>
